import styled, { css } from 'styled-components';

import { color, variable } from '../../helpers/style.helpers';

import { ListItemProps, StyledListItemProps } from './ListItem.decl';

import {
  Flex,
  Typography,
  Dropdown,
  IconButton,
  MenuVerticalFilled,
  useTheme,
  Menu,
  MenuItem,
  Spacer,
} from '@aircall/tractor';

const StyledListItem = styled(Flex)<StyledListItemProps>`
  position: relative;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  ${({ hasCustomHeight }) =>
    !hasCustomHeight &&
    css`
      &:hover {
        background-color: ${color.grey.lighter};
        transition: all 0.25s ease-in-out;
      }
    `}
`;

export const IconWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  padding: ${variable.space.xxs}px;
  border-radius: 50%;
  transition: all 0.25s ease-in-out;
`;

export const DropdownIconWrapper = styled(Flex)<StyledListItemProps>`
  align-items: center;
  justify-content: center;
  padding: ${variable.space.xxxs}px;
  cursor: pointer;

  &:hover {
    > ${IconWrapper} {
      background-color: ${color.grey.light};
    }
  }
  ${({ hasCustomHeight }) =>
    !hasCustomHeight
      ? css`
          border-left: 1px solid ${color.grey.light};
        `
      : css`
          position: absolute;
          top: 0;
          right: 0;
        `}
`;

export function ListItem({
  avatar,
  primaryText,
  secondaryText,
  children,
  dropdownOptions,
  'data-test': dataTest,
  ...otherProps
}: ListItemProps) {
  const { space } = useTheme();
  const { height } = otherProps;
  const hasCustomHeight = !!(height && height !== space.xl);

  return (
    <StyledListItem
      height={space.xl}
      data-test={dataTest}
      hasCustomHeight={hasCustomHeight}
      {...otherProps}
    >
      {primaryText && (
        <Spacer space='xxs' direction='horizontal' alignItems='center' px='s'>
          {avatar}
          <Typography variant='body2' data-test={`${dataTest}-primary-text`}>
            {primaryText}
          </Typography>
          {secondaryText && (
            <Typography
              variant='caption'
              color='text.dark'
              ml='xxs'
              data-test={`${dataTest}-secondary-text`}
            >
              {secondaryText}
            </Typography>
          )}
        </Spacer>
      )}
      {children}
      {dropdownOptions && (
        <Dropdown
          trigger={
            <DropdownIconWrapper hasCustomHeight={hasCustomHeight}>
              <IconWrapper>
                <IconButton
                  size={space.s}
                  component={MenuVerticalFilled}
                  color='grey.dark'
                  data-test={`${dataTest}-dropdown-trigger`}
                  discColor='transparent'
                  type='button'
                />
              </IconWrapper>
            </DropdownIconWrapper>
          }
          position='bottom'
          anchor='start'
        >
          <Menu>
            {dropdownOptions.map(({ title, callback }, index) => (
              <MenuItem
                key={title}
                onClick={callback}
                data-test={`${dataTest}-dropdown-menu-item-${index}`}
              >
                {title}
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
      )}
    </StyledListItem>
  );
}
